/**
 *
 * Button
 *
 */
import { memo, useMemo } from 'react';
import styled from 'styled-components/macro';

interface Props {
  children: React.ReactNode;
  color?: 'primary' | 'inactive' | 'active' | 'gradient' | 'transparent';
  shadow?: boolean;
  size?: 'small' | 'medium' | 'large';
  className?: string;
  onClick?: (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
}

const paddingMap = {
  small: 'px-3 py-1',
  medium: 'px-5 py-2',
  large: 'px-9 py-3',
};

export const ButtonComponent = memo((props: Props) => {
  const {
    color = 'primary',
    shadow = false,
    size = 'medium',
    className = '',
    children,
    ...others
  } = props;

  // Computed padding class from props
  const paddingClass = useMemo(() => paddingMap[size], [size]);
  // Computed background class from props
  const bgClass = useMemo(() => {
    let className = '';
    switch (color) {
      case 'gradient':
        className = `bg-button-gradient`;
        break;
      case 'active':
        className = `bg-active`;
        break;
      case 'inactive':
        className = `bg-inactive`;
        break;
      case 'transparent':
        className = `bg-transparent`;
        break;
      default:
        className = `bg-primary`;
        break;
    }
    return className;
  }, [color]);

  const textClass = useMemo(
    () => (color === 'active' ? 'text-black' : 'text-white'),
    [color],
  );
  const shadowClass = useMemo(() => (shadow ? 'shadow-sm' : ''), [shadow]);

  return (
    <button
      className={`rounded-full ${paddingClass} ${bgClass} ${textClass} ${shadowClass} ${className}`}
      {...others}
    >
      {children}
    </button>
  );
});
